import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { results } from "@/data/results";
import { scrollToId } from "@/lib/scroll";

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
};

const AUTO_MS = 3500;

export default function ResultsSection() {
  const trackRef = useRef(null);
  const [paused, setPaused] = useState(false);

  const step = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const cards = [...el.querySelectorAll("[data-card]")];
    const current = cards.findIndex((c) => c.offsetLeft >= el.scrollLeft - 8);
    const nextIdx = (current + dir + cards.length) % cards.length;
    const target = cards[nextIdx];
    const atEnd = dir > 0 && el.scrollLeft + el.clientWidth >= el.scrollWidth - 8;
    el.scrollTo({ left: atEnd ? 0 : target.offsetLeft, behavior: "smooth" });
  };

  useEffect(() => {
    if (paused || results.length < 2) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const id = setInterval(() => step(1), AUTO_MS);
    return () => clearInterval(id);
  }, [paused]);

  if (results.length === 0) return null;

  return (
    <section id="risultati" className="py-20 lg:py-24 border-y border-white/10 relative overflow-hidden" data-testid="results-section">
      <span aria-hidden="true" className="text-stroke font-display font-black uppercase absolute -top-6 right-0 text-[22vw] leading-none tracking-tight select-none pointer-events-none whitespace-nowrap">
        Risultati
      </span>

      <div className="max-w-7xl mx-auto relative z-10 px-5 sm:px-8">
        <motion.div {...fadeUp} transition={{ duration: 0.7 }} className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-blood font-semibold mb-4">// Prima &amp; dopo</p>
            <h2 className="font-display font-black uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
              I risultati
              <br />
              parlano <span className="text-blood">da soli.</span>
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="results-prev-btn" onClick={() => step(-1)} aria-label="Foto precedente" className="border border-white/15 hover:border-blood hover:text-blood p-3 transition-colors">
              <ArrowLeft size={18} />
            </button>
            <button data-testid="results-next-btn" onClick={() => step(1)} aria-label="Foto successiva" className="border border-white/15 hover:border-blood hover:text-blood p-3 transition-colors">
              <ArrowRight size={18} />
            </button>
          </div>
        </motion.div>
      </div>

      <div
        ref={trackRef}
        data-testid="results-track"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={() => setPaused(true)}
        onTouchEnd={() => setTimeout(() => setPaused(false), 4000)}
        className="results-track flex gap-4 sm:gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {results.map((r, i) => (
          <figure key={r.src} data-card data-testid={`results-card-${i}`} className="snap-start shrink-0">
            <div className="relative h-[360px] sm:h-[440px] lg:h-[520px] bg-ink-deep">
              <img src={r.src} alt={r.alt} loading={i < 3 ? "eager" : "lazy"} className="h-full w-auto max-w-[88vw] object-contain" draggable="false" />
              <span className="absolute top-3 left-3 bg-black/70 backdrop-blur font-mono text-[10px] uppercase tracking-[0.3em] text-white px-3 py-1.5">Prima</span>
              <span className="absolute top-3 right-3 bg-blood font-mono text-[10px] uppercase tracking-[0.3em] text-white px-3 py-1.5">Dopo</span>
            </div>
            <figcaption className="mt-3 border-l-2 border-blood pl-3">
              <p className="font-display font-bold uppercase tracking-wider text-sm leading-none text-blood" data-testid={`results-label-${i}`}>
                {r.result}
              </p>
              <p className="text-neutral-500 text-[11px] mt-1">{r.period}</p>
            </figcaption>
          </figure>
        ))}
      </div>

      <div className="max-w-7xl mx-auto relative z-10 px-5 sm:px-8 mt-8 flex flex-wrap items-center justify-between gap-4">
        <p className="text-neutral-500 text-xs">Persone reali, percorsi reali. Volti sfocati per rispetto della loro privacy.</p>
        <button
          data-testid="results-cta-btn"
          onClick={() => scrollToId("pacchetti")}
          className="group inline-flex items-center gap-3 bg-blood hover:bg-blood-hot text-white font-display font-bold uppercase tracking-wider px-6 py-3 transition-colors"
        >
          Voglio il mio risultato
          <ArrowRight size={18} className="transition-transform group-hover:translate-x-1.5" />
        </button>
      </div>
    </section>
  );
}
